/**
 * SC-476: the picture Safari's pop-out window shows when there is no camera it may show.
 *
 * Safari's window is one bare `<video>` with no cover to lay over it, and an element whose source goes empty
 * makes Safari close the window. So where the window would otherwise have nothing, or only a camera held for
 * its background (whose last frame is the real room, SC-158), it gets a neutral picture instead: the tile
 * colour with a caption, drawn on a canvas and captured as a stream track. Two cases, one picture each:
 *
 * - `waiting`: someone's camera is held for their background. Same caption and colours as the in-page cover.
 * - `idle`: nobody has a camera on to show, or it is not known yet whether a camera that just went off is a
 *   hold (see `SAFARI_OFF_SETTLE_MS` in `VideoPipRenderer`).
 *
 * Colours are never literals. A canvas cannot take `var(--sevv-*)`, so the same expressions the in-page cover
 * uses are resolved by the browser on a hidden probe element each time the picture is drawn, which also
 * makes a theme change reach it within a repaint.
 */

import type { TrackReference } from '@livekit/components-react'

import {
  CAMERA_WAITING_COLOR,
  CAMERA_WAITING_LABEL,
  VIDEO_TILE_COVER_BACKGROUND,
} from '../../components/CameraWaitingPlaceholder'
import { streamTrackRef } from './streamTrackRef'

export type NeutralPictureKind = 'waiting' | 'idle'

export const NO_CAMERA_LABEL = 'No camera on'

const PICTURES: Record<NeutralPictureKind, { label: string; colour: string }> = {
  waiting: { label: CAMERA_WAITING_LABEL, colour: CAMERA_WAITING_COLOR },
  // Muted on purpose: amber says "a camera is coming", and nothing is.
  idle: { label: NO_CAMERA_LABEL, colour: 'var(--sevv-gray-400, currentColor)' },
}

const DEFAULT_SIZE = { width: 640, height: 360 }
// A low simulcast layer can be tiny, and a caption drawn that small is unreadable once the window is
// bigger; a huge capture is cost for a flat colour.
const MIN_WIDTH = 480
const MAX_WIDTH = 1280

/**
 * Safari emits a frame from a canvas stream only when something is drawn on the canvas, so a still picture
 * would give the element nothing to show until the next repaint. Drawn again on a timer while it is on the
 * element (and once as it goes on), which also keeps a track in a throttled background tab flowing.
 */
export const NEUTRAL_REPAINT_MS = 1_000

export interface NeutralPictureSize {
  width: number
  height: number
}

export interface NeutralPictures {
  /**
   * The picture to attach, sized like the picture it replaces when that is known: the window follows the
   * video's aspect ratio, so a neutral picture of another shape would resize the window.
   * `undefined` where the browser cannot capture a canvas; the caller then has no picture to offer.
   */
  refFor(kind: NeutralPictureKind, size?: NeutralPictureSize): TrackReference | undefined
  destroy(): void
}

interface Picture {
  ref: TrackReference
  canvas: HTMLCanvasElement
  stop(): void
  resize(size: NeutralPictureSize | undefined): void
}

function fit(size: NeutralPictureSize | undefined): NeutralPictureSize {
  const width = size?.width ?? 0
  const height = size?.height ?? 0
  if (!(width > 0) || !(height > 0)) return DEFAULT_SIZE
  const scale = width > MAX_WIDTH ? MAX_WIDTH / width : width < MIN_WIDTH ? MIN_WIDTH / width : 1
  return { width: Math.round(width * scale), height: Math.round(height * scale) }
}

export function createNeutralPictures(doc: Document): NeutralPictures {
  const pictures = new Map<NeutralPictureKind, Picture>()
  let probe: HTMLElement | null = null
  /**
   * A browser that cannot capture a canvas cannot later; without this every model update while a window is
   * open would create another canvas, find it unusable and throw it away.
   */
  let unsupported = false

  /** Hidden but in the tree: custom properties only resolve on an element that inherits from the root. */
  const resolve = (colour: string) => {
    if (!probe) {
      probe = doc.createElement('span')
      probe.setAttribute('aria-hidden', 'true')
      probe.style.display = 'none'
      doc.body.appendChild(probe)
    }
    probe.style.backgroundColor = VIDEO_TILE_COVER_BACKGROUND
    probe.style.color = colour
    probe.style.fontFamily = 'var(--sevv-font-family, sans-serif)'
    const computed = (doc.defaultView ?? window).getComputedStyle(probe)
    return {
      background: computed.backgroundColor,
      colour: computed.color,
      fontFamily: computed.fontFamily,
    }
  }

  const build = (
    kind: NeutralPictureKind,
    size: NeutralPictureSize | undefined
  ): Picture | null => {
    const canvas = doc.createElement('canvas')
    // Opaque, so a translucent tile colour is composited against black the same way on every browser
    // instead of being left to however each one flattens a frame with alpha.
    const context = canvas.getContext?.('2d', { alpha: false })
    const capture = (canvas as HTMLCanvasElement & { captureStream?: () => MediaStream })
      .captureStream
    if (!context || typeof capture !== 'function') return null
    // Sized before it is captured: a stream whose first frames are one shape and the next another would
    // resize the window as it appears.
    const initial = fit(size)
    canvas.width = initial.width
    canvas.height = initial.height
    const media = capture.call(canvas).getVideoTracks()[0]
    if (!media) return null

    const { label, colour } = PICTURES[kind]
    let timer: ReturnType<typeof setInterval> | null = null

    const paint = () => {
      const { width, height } = canvas
      const look = resolve(colour)
      context.clearRect(0, 0, width, height)
      if (look.background) context.fillStyle = look.background
      context.fillRect(0, 0, width, height)
      if (look.colour) context.fillStyle = look.colour
      const family = look.fontFamily || 'sans-serif'
      const room = width * 0.9
      // From the shorter side: a portrait window (720x1280) sized from its height would get text twice as
      // wide as it can be, which `maxWidth` then squeezes into a distorted caption.
      let fontSize = Math.max(12, Math.round(Math.min(width, height) * 0.08))
      context.font = `600 ${fontSize}px ${family}`
      context.textAlign = 'center'
      context.textBaseline = 'middle'
      // Smaller rather than squeezed where the caption is still too wide (a narrow window, a long label).
      const measured = context.measureText?.(label)?.width
      if (measured && measured > room) {
        fontSize = Math.max(12, Math.floor((fontSize * room) / measured))
        context.font = `600 ${fontSize}px ${family}`
      }
      // `maxWidth` is the last resort, for what is too wide even at the smallest size.
      context.fillText(label, width / 2, height / 2, room)
    }
    const stop = () => {
      if (timer !== null) clearInterval(timer)
      timer = null
    }

    paint()

    const ref = streamTrackRef(media, {
      onAttach: () => {
        paint()
        if (timer === null) timer = setInterval(paint, NEUTRAL_REPAINT_MS)
      },
      onDetach: stop,
    })

    return {
      ref,
      canvas,
      stop: () => {
        stop()
        media.stop()
      },
      resize(size) {
        const next = fit(size)
        if (canvas.width === next.width && canvas.height === next.height) return
        canvas.width = next.width
        canvas.height = next.height
        paint()
      },
    }
  }

  return {
    refFor(kind, size) {
      let picture = pictures.get(kind)
      if (!picture) {
        if (unsupported) return undefined
        const built = build(kind, size)
        if (!built) {
          unsupported = true
          return undefined
        }
        pictures.set(kind, built)
        picture = built
      }
      // Only a size the caller knows: the picture keeps the last one when the element has none yet.
      if (size && size.width > 0 && size.height > 0) picture.resize(size)
      return picture.ref
    },
    destroy() {
      pictures.forEach((picture) => picture.stop())
      pictures.clear()
      probe?.remove()
      probe = null
    },
  }
}
